import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Reveal } from '../components/Reveal';
import { GridRails } from '../components/GridRails';
import { GridDivider } from '../components/GridDivider';
import type { LegalDoc, LegalSection } from '../data/legal';
import { legalDocs } from '../data/legal';
import { usePageMeta } from '../hooks/usePageMeta';
import { legalSeoDescription } from '../data/seo';
type Props = {
  doc: LegalDoc;
};
const inner = 'relative z-10 mx-auto max-w-[1720px] px-5 md:px-16 lg:px-24';
const label = 'font-accent text-[11px] uppercase tracking-[0.08em]';
const anchor = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

/** Simple text page for Imprint, Privacy, Cookies and Terms. */
export function Legal({
  doc
}: Props) {
  usePageMeta({
    title: `${doc.label} | KAYS & KAYS`,
    description: legalSeoDescription(doc.label)
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [doc]);
  return <>
      {/* Navy band under the header */}
      <section className="relative bg-navy pb-16 pt-[180px] text-cream md:pb-20 md:pt-[220px]" data-id="element-502" data-mp="27kd6aspm6f5t">
        <div className={inner} data-id="element-503" data-mp="23kb149ko73ls">
          <Reveal data-id="element-504" data-mp="23j036qdwoq2l">
            <p className={`${label} flex items-center gap-3 text-cream/70`} data-id="element-505" data-mp="23lvy05ugiymt">
              <span aria-hidden="true" className="inline-block h-[6px] w-[6px] bg-cream" data-id="element-506" data-mp="2hisp88ehmi48" />
              Legal
            </p>
            <h1 className="mt-6 font-display text-[44px] leading-[0.98] tracking-[-0.035em] md:text-[72px]" data-id="element-507" data-mp="2cierbj6loojx">
              {doc.title} <span className="font-editorial italic tracking-[-0.015em]" data-id="element-508" data-mp="1jvtuw4b9pm9i">{doc.titleAccent}</span>
            </h1>
            <p className="mt-6 max-w-[560px] text-[14.5px] leading-[1.55] text-cream/65" data-id="element-509" data-mp="1ykq25z5i2v92">{doc.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="relative bg-white text-ink" data-id="element-510" data-mp="1trn87pwt9jsc">
        <GridRails data-id="element-511" data-mp="1633zj99un4zq" />
        <div className={`${inner} grid gap-12 px-9 py-16 md:grid-cols-[220px_1fr] md:gap-20 md:py-24`} data-id="element-512" data-mp="162ktoqxh4722">
          {/* Contents + other documents */}
          <aside className="md:sticky md:top-12 md:self-start" data-id="element-513" data-mp="317k2y2rkkbhv">
            <Reveal data-id="element-514" data-mp="3157qu68hsvcq">
            <p className={`${label} text-ink/50`} data-id="element-515" data-mp="366eym5amxuje">Last updated</p>
            <p className="mt-2 text-[13px] text-ink/80" data-id="element-516" data-mp="364kbqlfszsco">{doc.updated}</p>

            <p className={`${label} mt-10 hidden text-ink/50 md:block`} data-id="element-517" data-mp="3ay6glrfq6rxx">Contents</p>
            <ul className="mt-4 hidden space-y-2 text-[13px] md:block" data-id="element-518" data-mp="3eyyoohcv2qsm">
              {doc.sections.map(s => <li key={s.heading} data-id="element-519" data-mp="2i9hm4b96dkv5">
                  <a href={`#${anchor(s.heading)}`} className="link-soft no-arrow text-ink/70" data-id="element-520" data-mp="2iaj0oppegphm">
                    {s.heading}
                  </a>
                </li>)}
            </ul>

            <p className={`${label} mt-10 text-ink/50`} data-id="element-521" data-mp="2rbh4p7zu8x07">Documents</p>
            <ul className="mt-4 space-y-2 text-[13px]" data-id="element-522" data-mp="2w9sejiq4v7if">
              {legalDocs.map(d => <li key={d.slug} data-id="element-523" data-mp="6i3z23l2r2wh">
                  {d.slug === doc.slug ? <span className="flex items-center gap-2 text-ink" data-id="element-524" data-mp="bh6ztbu0umyn">
                      <span aria-hidden="true" className="inline-block h-[5px] w-[5px] bg-ink" data-id="element-525" data-mp="llniecyvyi65" />
                      {d.label}
                    </span> : <Link to={`/${d.slug}`} className="link-soft text-ink/70" data-id="element-526" data-mp="gp6hsjai2bc6">
                      {d.label}
                    </Link>}
                </li>)}
            </ul>
            </Reveal>
          </aside>

          <article className="max-w-[680px]" data-id="element-527" data-mp="2wgtg87i0ubw">
            {doc.sections.map((s, i) => <section key={s.heading} id={anchor(s.heading)} className={`scroll-mt-12 ${i > 0 ? 'mt-10 border-t border-mist-line pt-10' : ''}`} data-id="element-528" data-mp="3u1gw6k1u1fji" data-mpi={i}>
                <Reveal data-id="element-529" data-mp="3l23lxp5vwmxq">
                  <h2 className={`${label} flex items-center gap-3 text-ink/80`} data-id="element-530" data-mp="3l2v7e75rp4aa">
                    <span className="tabular-nums text-ink/40" data-id="element-531" data-mp="1n9zf2h5zoz31">{String(i + 1).padStart(2, '0')}</span>
                    {s.heading}
                  </h2>
                  <div className="mt-4 space-y-3 text-[14.5px] leading-[1.6] text-ink/70" data-id="element-532" data-mp="1qd56ymwrzdnz">
                    {s.body.map(p => <p key={p} data-id="element-533" data-mp="1vanh2gmj3yxk">{withLinks(p, s.links)}</p>)}
                  </div>
                  {s.creditLinks && <ul className="mt-6 flex flex-wrap gap-x-8 gap-y-3" data-id="element-534" data-mp="1hejoerrazyn7">
                      {s.creditLinks.map(c => <li key={c.href} data-id="element-535" data-mp="17lvwaab9qiut">
                          <Link to={c.href} className={`${label} link-soft no-arrow inline-flex items-center gap-2 text-ink/80`} data-id="element-536" data-mp="17kt84a5byuil">
                            {c.label} →
                          </Link>
                        </li>)}
                    </ul>}
                </Reveal>
              </section>)}
            <p className="mt-14 text-[13px] text-ink/50" data-id="element-537" data-mp="tmczuertkvyc">
              <Link to="/" className="link-soft no-arrow text-ink/70" data-id="element-538" data-mp="tltgquqfo1iq">
                ← Back to KAYS &amp; KAYS
              </Link>
            </p>
          </article>
        </div>
      </section>
      <GridDivider data-id="element-539" data-mp="53ayusvs70mn" />
    </>;
}

/** Turns listed phrases inside a paragraph into external links. */
function withLinks(text: string, links?: LegalSection['links']): React.ReactNode {
  if (!links?.length) return text;
  const parts: React.ReactNode[] = [];
  let rest = text;
  let key = 0;
  while (rest) {
    let hit: {
      i: number;
      l: {
        text: string;
        href: string;
      };
    } | null = null;
    for (const l of links) {
      const i = rest.indexOf(l.text);
      if (i >= 0 && (!hit || i < hit.i)) hit = {
        i,
        l
      };
    }
    if (!hit) {
      parts.push(rest);
      break;
    }
    if (hit.i > 0) parts.push(rest.slice(0, hit.i));
    parts.push(<a key={key++} href={hit.l.href} target="_blank" rel="noopener noreferrer" className="text-ink underline decoration-ink/30 underline-offset-[3px] transition-[text-decoration-color] duration-200 hover:decoration-ink" data-id="element-540" data-mp="1bpwyvuyrjog8">
        {hit.l.text}
      </a>);
    rest = rest.slice(hit.i + hit.l.text.length);
  }
  return parts;
}